import { useQuery } from '@tanstack/react-query';
import { type AbilitySummary, type AuthenticatedUser, type Permission } from '@apm/contracts';
import { useOptionalUser } from '../features/auth/auth-context';
import { apiFetch } from './http';

/**
 * Which app shell a session gets — APM-PSPACE-g, docs/plan-personal-space.md §3.4.
 *
 * `limited` covers **two** sessions with the same needs: the personal space
 * (`isPersonal`) and a session in an organisation where one is a `GUEST`. Both
 * work inside projects and nothing else — the org-wide pages (dashboard,
 * performance, OKR, absences, files, org chart…) either 403 for them or are
 * empty by construction. `CLIENT` is not here: it still goes to `/portal`.
 */
export type ShellMode = 'full' | 'limited';

export function shellModeOf(user: Pick<AuthenticatedUser, 'isPersonal' | 'orgRole'>): ShellMode {
  return user.isPersonal || user.orgRole === 'GUEST' ? 'limited' : 'full';
}

/** `full` when there is no session (tests without an `AuthProvider`). */
export function useShellMode(): ShellMode {
  const user = useOptionalUser();
  return user ? shellModeOf(user) : 'full';
}

/** Where `/` and every route outside {@link limitedShellAllows} land in the limited shell. */
export const LIMITED_SHELL_HOME = '/projects';

/**
 * Routes the limited shell keeps — the three nav items, what the account menu
 * and the bell link to, and the invite links a personal-space owner uses to
 * **leave** the personal space (`PersonalSpaceCard` "Kiểm tra lời mời").
 *
 * Exact paths or a prefix ending in `/`. Everything else (typed by hand, an old
 * bookmark, a link from a full-shell session) goes to {@link LIMITED_SHELL_HOME}.
 *
 * ⚠️ Experience, not a gate: the API answers 403 on its own. This only spares
 * the user a page that fires a dozen calls and shows a dozen red cards.
 */
const LIMITED_PATHS: readonly string[] = [
  '/projects',
  '/my-tasks',
  '/issues',
  '/notifications',
  '/settings',
  '/privacy',
  '/invite/',
  '/join/',
];

export function limitedShellAllows(pathname: string): boolean {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  return LIMITED_PATHS.some((allowed) =>
    allowed.endsWith('/') ? path.startsWith(allowed) : path === allowed,
  );
}

/**
 * May the limited shell call an endpoint guarded by `permission`? — APM-PSPACE-g.
 *
 * `true` in the full shell (it keeps calling as before: org roles there carry many
 * permissions through org grants, and a wrong guess would hide data from someone
 * allowed to see it). In the limited shell: `undefined` until `/me/abilities` is
 * known, then whether the session holds `permission` globally or — with `projectId`
 * in that project, without it in **any** project (the `canPotentially` question the
 * API's `PermissionGuard` asks of a route without `:projectId`).
 *
 * Reads the shell's own `['abilities']` query, so it costs no extra call. This is
 * how the shell honours "do not call what answers 403" without widening the API.
 */
export function useLimitedShellCan(
  permission: Permission,
  projectId?: string,
): boolean | undefined {
  const limited = useShellMode() === 'limited';
  const abilities = useQuery({
    queryKey: ['abilities'],
    queryFn: () => apiFetch<AbilitySummary>('/me/abilities'),
    enabled: limited,
    staleTime: 5 * 60_000,
  });
  if (!limited) return true;
  if (!abilities.data) return abilities.isError ? false : undefined;
  if (abilities.data.global.includes(permission)) return true;
  return abilities.data.perProject.some(
    (entry) =>
      (projectId === undefined || entry.projectId === projectId) &&
      entry.permissions.includes(permission),
  );
}
